/**
 * Espace Famille — enregistrement des pièces jointes sur mobile (#2563).
 *
 * WebKit n'expose aucun gestionnaire de téléchargement à une PWA installée :
 * ni `Content-Disposition: attachment` ni l'attribut `download` n'y ont de
 * cible, et le lien part dans Safari, qui affiche le PDF au lieu de
 * l'enregistrer. Le partage de fichiers est la seule API que WebKit offre pour
 * ce cas : elle ouvre la feuille système, « Enregistrer dans Fichiers » compris.
 *
 * Le module vaut pour tout mobile, PWA ou navigateur. Android n'en a pas
 * besoin — son téléchargement natif fonctionne — mais le faire passer par le
 * même chemin évite de soutenir quatre comportements là où un seul suffit ; le
 * prix en est un geste de plus pour choisir la destination.
 *
 * Sur ordinateur, le lien natif fait le travail et ce module ne s'interpose pas.
 *
 * Tout lien porteur d'un attribut `download` est couvert, sans marqueur
 * supplémentaire dans les vues.
 */

const BUSY_CLASS = 'is-downloading';

/**
 * La plateforme, et non le mode d'affichage.
 *
 * `display-mode: standalone` et `navigator.standalone` rapportent tous deux
 * `false` sur iOS 27 dans une PWA pourtant installée : s'y fier laissait ce
 * module inerte là où on en a le plus besoin. On ne cherche donc pas à savoir
 * si la page tourne dans la PWA, seulement si l'appareil est mobile — la
 * feuille de partage y est le chemin retenu pour tout le monde, de sorte qu'un
 * seul comportement soit à soutenir sur iOS comme sur Android.
 *
 * L'ordinateur garde le lien natif : son téléchargement est sans ambiguïté.
 */
function isMobile() {
    const ua = window.navigator.userAgent;

    return /iPad|iPhone|iPod|Android/.test(ua)
        || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

function canShareFiles() {
    return typeof navigator.share === 'function'
        && typeof navigator.canShare === 'function';
}

function downloadLinkFrom(target) {
    return target instanceof Element ? target.closest('a[download]') : null;
}

/**
 * Le nom que le serveur a choisi, quand le lien n'en porte pas.
 *
 * `filename*` prime sur `filename` : c'est la forme qui transporte les accents,
 * et Symfony émet les deux (RFC 6266).
 */
function fileNameFromDisposition(header) {
    if (!header) {
        return '';
    }

    const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);

    if (extended !== null) {
        try {
            return decodeURIComponent(extended[1].trim());
        } catch (e) {
            // Suite mal encodée : le nom simple prendra le relais.
        }
    }

    const quoted = /filename\s*=\s*"([^"]+)"/i.exec(header)
        || /filename\s*=\s*([^;]+)/i.exec(header);

    return quoted !== null ? quoted[1].trim() : '';
}

/**
 * Le lien porte le nom voulu ; sinon celui de la réponse ; en dernier ressort
 * le segment d'URL. iOS choisit sur l'extension l'application qui ouvrira le
 * fichier enregistré : un nom sans extension donne un fichier inerte, et une
 * URL comme /documents/attestation-scolarite n'en fournit aucune.
 */
function fileNameFor(link, response) {
    const explicit = (link.getAttribute('download') || '').trim();

    if (explicit !== '') {
        return explicit;
    }

    const served = fileNameFromDisposition(response.headers.get('Content-Disposition'));

    if (served !== '') {
        return served;
    }

    const segments = new URL(link.href, window.location.origin).pathname.split('/');

    return decodeURIComponent(segments.pop() || '') || 'document';
}

async function fileFor(link) {
    const response = await fetch(link.href, { credentials: 'same-origin' });

    if (!response.ok) {
        throw new Error('HTTP ' + response.status);
    }

    const blob = await response.blob();

    // Un type vide fait refuser le partage par iOS, qui ne sait alors pas quoi
    // proposer comme destination.
    return new File([blob], fileNameFor(link, response), {
        type: blob.type || 'application/octet-stream',
    });
}

/**
 * Repli : la navigation d'origine, c'est-à-dire le comportement d'avant ce
 * module — un téléchargement sur Android, un détour par Safari sous iOS. Le
 * parent obtient son document dans les deux cas ; mieux vaut cela qu'un clic
 * sans effet.
 */
function fallback(link) {
    window.location.href = link.href;
}

document.addEventListener('click', async (event) => {
    const link = downloadLinkFrom(event.target);

    if (link === null || !isMobile() || !canShareFiles()) {
        return;
    }

    event.preventDefault();

    // Le téléchargement précède l'ouverture de la feuille : sans repère visuel,
    // le premier clic semble sans effet. WebKit garde l'autorisation du geste
    // cinq secondes, ce qui laisse la place à cette attente ; au-delà, share()
    // est refusé et le repli s'applique.
    link.classList.add(BUSY_CLASS);

    let file;

    try {
        file = await fileFor(link);
    } catch (e) {
        fallback(link);

        return;
    } finally {
        link.classList.remove(BUSY_CLASS);
    }

    if (!navigator.canShare({ files: [file] })) {
        fallback(link);

        return;
    }

    try {
        await navigator.share({ files: [file] });
    } catch (e) {
        // Le refus de l'utilisateur n'est pas un échec : la feuille s'est
        // ouverte, il l'a fermée. Toute autre erreur rend la main au lien.
        if (e && e.name !== 'AbortError') {
            fallback(link);
        }
    }
});
